<script lang="ts">
/**
 * Two-column “manufacturer battle” `.cc`: split bar sized from `left.barValue` vs `right.barValue`, plus paired stat boxes.
 * Title / subtitle use `{@html}` for entities.
 *
 * Each side's accent — split-bar segment, headline figure and box tint — follows its `barColor`,
 * so a slide whose other charts already assign colours per agent can keep this card consistent with
 * them. Defaults are the original teal (left) / purple (right) if `barColor` is omitted.
 */
export type StudyBattleCardSide = {
  label: string
  valueDisplay: string
  sharePct: string
  /** Numeric total used to size the split bar (e.g. claim count). */
  barValue: number
  /** Accent for this side — split-bar segment, headline figure and box tint. Defaults teal
   *  (left) / purple (right). */
  barColor?: string
}

interface Props {
  title: string
  subtitle: string
  left: StudyBattleCardSide
  right: StudyBattleCardSide
  cardClass?: string
  cardStyle?: string
}

let { title, subtitle, left, right, cardClass = '', cardStyle }: Props = $props()

const totalBar = $derived(left.barValue + right.barValue || 1)
const leftBarW = $derived((left.barValue / totalBar) * 100)
const rightBarW = $derived((right.barValue / totalBar) * 100)
const leftSegColor = $derived(left.barColor ?? 'var(--g)')
const rightSegColor = $derived(right.barColor ?? '#a78bfa')
/** Box tint/border derived from the accent. `color-mix` keeps one source of truth per side rather
 *  than asking callers to supply a matching translucent fill. */
const leftFill = $derived(`color-mix(in srgb, ${leftSegColor} 10%, transparent)`)
const leftBorder = $derived(`color-mix(in srgb, ${leftSegColor} 30%, transparent)`)
const rightFill = $derived(`color-mix(in srgb, ${rightSegColor} 10%, transparent)`)
const rightBorder = $derived(`color-mix(in srgb, ${rightSegColor} 30%, transparent)`)
</script>

<div class="cc {cardClass}" style={cardStyle}>
  <div class="ct">{@html title}</div>
  <div class="cs">{@html subtitle}</div>
  <div
    style="display:flex;height:5px;border-radius:3px;overflow:hidden;margin-top:.48rem;border:1px solid var(--gbr);"
    aria-hidden="true"
  >
    <div style="width:{leftBarW}%;background:{leftSegColor};flex-shrink:0;"></div>
    <div style="width:{rightBarW}%;background:{rightSegColor};flex-shrink:0;"></div>
  </div>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:.58rem;margin-top:.68rem;">
    <div
      style="text-align:center;padding:.85rem;background:{leftFill};border:1px solid {leftBorder};border-radius:8px;"
    >
      <div
        style="font-size:.56rem;color:var(--mu);letter-spacing:.1em;text-transform:uppercase;margin-bottom:.32rem;"
      >
        {left.label}
      </div>
      <div style="font-family:'DM Serif Display',serif;font-size:1.4rem;color:{leftSegColor};">
        {left.valueDisplay}
      </div>
      <div style="font-size:.6rem;color:var(--mu);margin-top:.16rem;">
        {left.sharePct}% share
      </div>
    </div>
    <div
      style="text-align:center;padding:.85rem;background:{rightFill};border:1px solid {rightBorder};border-radius:8px;"
    >
      <div
        style="font-size:.56rem;color:var(--mu);letter-spacing:.1em;text-transform:uppercase;margin-bottom:.32rem;"
      >
        {right.label}
      </div>
      <div style="font-family:'DM Serif Display',serif;font-size:1.4rem;color:{rightSegColor};">
        {right.valueDisplay}
      </div>
      <div style="font-size:.6rem;color:var(--mu);margin-top:.16rem;">
        {right.sharePct}% share
      </div>
    </div>
  </div>
</div>
